import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import PersonRemoveAlt1Icon from "@mui/icons-material/PersonRemoveAlt1";
import SmsRoundedIcon from "@mui/icons-material/SmsRounded";
import BusinessRoundedIcon from "@mui/icons-material/BusinessRounded";
import { Avatar, Box, Chip, IconButton, List, ListItem, Tooltip, Typography } from "@mui/material";
import { formatUSPhone } from "../../../utils";
import { useContactCenterTheme } from "../../virtual-receptionist/theming/ContactCenterThemeContext";
import { MessageGroupMemberType, MessageGroupSummary } from "../messageGroups.types";
import { getAvatarColor, getMemberFullName, getMemberRoleLabel } from "../utils/messageGroupDisplay.utils";

interface GroupMembersPanelProps {
  open: boolean;
  group?: MessageGroupSummary | null;
  canManage: boolean;
  isRemoving?: boolean;
  onClose: () => void;
  onAddMembers: () => void;
  onRemoveMember: (member: { type: MessageGroupMemberType; uid: string; name?: string }) => void;
}

export const GroupMembersPanel = ({
  open,
  group,
  canManage,
  isRemoving,
  onClose,
  onAddMembers,
  onRemoveMember,
}: GroupMembersPanelProps) => {
  const { theme } = useContactCenterTheme();
  const members = group?.members ?? [];

  return (
    <Box
      aria-hidden={!open}
      sx={{
        position: "absolute",
        inset: 0,
        zIndex: 6,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        backgroundColor: theme.shell.bg,
        transform: open ? "translateX(0)" : "translateX(100%)",
        visibility: open ? "visible" : "hidden",
        transition: "transform 0.22s ease, visibility 0.22s ease",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          px: 1,
          py: 0.5,
          minHeight: 44,
          flexShrink: 0,
          borderBottom: "1px solid",
          borderColor: theme.shell.border,
          backgroundColor: theme.shell.headerBg,
        }}
      >
        <IconButton size="small" aria-label="Back to conversation" onClick={onClose} sx={{ color: theme.shell.textSecondary }}>
          <ArrowBackIcon sx={{ fontSize: 18 }} />
        </IconButton>

        <Typography sx={{ flex: 1, fontSize: 13.5, fontWeight: 600, color: theme.shell.textPrimary }}>
          Members ({group?.membersCount ?? 0})
        </Typography>

        {canManage && (
          <Tooltip title="Add members" arrow>
            <IconButton size="small" aria-label="Add members" onClick={onAddMembers} sx={{ color: theme.shell.accent }}>
              <PersonAddAlt1Icon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          "&::-webkit-scrollbar": { width: 6 },
          "&::-webkit-scrollbar-thumb": { bgcolor: theme.chat.scrollThumb, borderRadius: "3px" },
        }}
      >
        <List dense disablePadding>
          {members.map((member) => {
            const isFlownectNumber = member.type === "companyNumber";
            const isExternal = member.type === "contact" || isFlownectNumber;
            const isOwnNumber = Boolean(group?.smsFromNumber && member.phoneNumber && member.phoneNumber === group.smsFromNumber);
            const phoneLabel = member.phoneNumber ? formatUSPhone(member.phoneNumber) || member.phoneNumber : null;
            const resolvedName = getMemberFullName(member) || phoneLabel || "Unknown";
            const name = isOwnNumber ? `${resolvedName} (You)` : resolvedName;
            const role = isExternal ? phoneLabel : getMemberRoleLabel(member);

            return (
              <ListItem
                key={`${member.type}:${member.uid}`}
                sx={{ gap: 1.25, py: 0.9, px: 1.5 }}
                secondaryAction={
                  canManage ? (
                    <Tooltip title="Remove from group" arrow>
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => onRemoveMember({ type: member.type, uid: member.uid, name })}
                          aria-label={`Remove ${name}`}
                          disabled={isRemoving || isOwnNumber}
                          sx={{ color: theme.shell.textSecondary }}
                        >
                          <PersonRemoveAlt1Icon sx={{ fontSize: 17 }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  ) : null
                }
              >
                <Avatar
                  src={member.avatar || undefined}
                  alt={name}
                  sx={{ width: 32, height: 32, fontSize: "0.75rem", bgcolor: member.avatar ? undefined : getAvatarColor(name) }}
                >
                  {name[0]?.toUpperCase() || "?"}
                </Avatar>

                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
                    <Typography noWrap sx={{ fontSize: 12.5, fontWeight: 600, color: theme.shell.textPrimary }}>
                      {name}
                    </Typography>
                    {member.isAdmin && <Chip label="Admin" size="small" sx={{ height: 16, fontSize: 9, flexShrink: 0 }} />}
                    {isExternal && (
                      <Chip
                        icon={
                          isFlownectNumber ? <BusinessRoundedIcon sx={{ fontSize: 11 }} /> : <SmsRoundedIcon sx={{ fontSize: 11 }} />
                        }
                        label={isFlownectNumber ? "Flownect" : "SMS"}
                        size="small"
                        color={isFlownectNumber ? "info" : "default"}
                        sx={{ height: 16, fontSize: 9, flexShrink: 0 }}
                      />
                    )}
                  </Box>
                  {role && (
                    <Typography
                      noWrap
                      sx={{ fontSize: 10.5, color: theme.shell.textSecondary, textTransform: isExternal ? "none" : "capitalize" }}
                    >
                      {role}
                    </Typography>
                  )}
                </Box>
              </ListItem>
            );
          })}
        </List>
      </Box>
    </Box>
  );
};
